import { call, put, takeLatest, select } from 'redux-saga/effects';
import {
    CampaignsUpdateFilterAction,
    CampaignsUpdateFilterActionType,
    FetchCampaignsAction,
    FetchCampaignsActionType,
    load,
    loadError,
    loadSuccess,
    loadSuccessWithFilter,
} from '@copilot/common/pages/campaigns/data/actionCreators';
import { CampaignManager } from '@copilot/data';
import { CampaignActions } from '@copilot/common/store/actions/campaign';
import { filterThenMapElements } from '@copilot/common/utils/common/array';
import { CampaignsFilters } from '../types';
import { CampaignsFilterRequestModel } from '@copilot/data/requests/models';
import { getPagedCampaigns } from './selectors';
import { CampaignModelState } from './reducer';
import createSagaMiddleware from 'redux-saga';

/**
 * Build the CampaignsFilterRequestModel from filters
 * @param filter
 */
const convertFilterToRequestModel = (
    filter: CampaignsFilters
): Partial<CampaignsFilterRequestModel> => ({
    OrgMemberId: {
        include: filterThenMapElements(
            filter.teamMembers,
            (f) => f.isVisible,
            (f) => f.key
        ),
        exclude: [],
    },
});

function* fetchAllAsync(action: FetchCampaignsAction) {
    try {
        yield put(load());
        const payload: PromiseFactoryThenType<typeof CampaignManager.getCampaignsByOrganization> =
            yield call(
                CampaignManager.getCampaignsByOrganization,
                action.organizationId,
                action.pageNumber,
                action.pageSize,
                action.searchTerm
            );
        yield put(CampaignActions.loadCampaigns(payload.results)); // [TODO] THREE8-97 Hack to fix issues caused by aggressive caching
        yield put(loadSuccess(payload));
    } catch (error) {
        yield put(loadError(error));
    }
}

/**
 * Fetch campaigns by updated filters
 * @param action
 */
function* updateFilterAsync(action: CampaignsUpdateFilterAction<CampaignsFilters>) {
    try {
        yield put(load());
        const currentState: undefined | CampaignModelState<CampaignsFilters> =
            yield select(getPagedCampaigns);
        const filter = convertFilterToRequestModel({ ...currentState?.filter, ...action.filter });
        // TODO: Replace hardcoded pagination with currenState
        const payload: PromiseFactoryThenType<typeof CampaignManager.getCampaignsByOrganization> =
            yield call(
                CampaignManager.getCampaignsByOrganization,
                action.orgId,
                0,
                1000,
                action.searchTerm,
                filter
            );
        yield put(CampaignActions.loadCampaigns(payload.results));
        yield put(loadSuccessWithFilter(payload, filter, action.searchTerm));
    } catch (error) {
        yield put(loadError(error));
    }
}

/**
 * Watches the fetch campaigns action.
 */
function* watchFetchCampaigns() {
    yield takeLatest(FetchCampaignsActionType, fetchAllAsync);
}

function* watchUpdateFilter() {
    yield takeLatest(CampaignsUpdateFilterActionType, updateFilterAsync);
}

/**
 * Apply Campaigns sagas to the saga middleware
 * @param sagaMiddleware
 */
export const registerCampaignsSaga = (
    sagaMiddleware: ReturnType<typeof createSagaMiddleware>
): void => {
    sagaMiddleware.run(watchFetchCampaigns);
    sagaMiddleware.run(watchUpdateFilter);
};
